import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import SfLayout from "./components/SfLayout";
import Sf1Inizia from "./pages/Sf1Inizia";
import { initPixels, getConsent, track } from "./lib/analytics";

// Code splitting: solo la schermata 1 è nel bundle iniziale; le altre viaggiano in
// chunk separati e vengono precaricate in background subito dopo il primo caricamento.
const Sf2ChiSiamo = lazy(() => import("./pages/Sf2ChiSiamo"));
const Sf3Referenze = lazy(() => import("./pages/Sf3Referenze"));
const Sf4Video = lazy(() => import("./pages/Sf4Video"));
const Sf5Soluzione = lazy(() => import("./pages/Sf5Soluzione"));
const Sf6Azienda = lazy(() => import("./pages/Sf6Azienda"));
const Sf7Contatto = lazy(() => import("./pages/Sf7Contatto"));
const Admin = lazy(() => import("./pages/Admin"));

const PRELOAD_SCREENS = [
  () => import("./pages/Sf2ChiSiamo"),
  () => import("./pages/Sf3Referenze"),
  () => import("./pages/Sf4Video"),
  () => import("./pages/Sf5Soluzione"),
  () => import("./pages/Sf6Azienda"),
  () => import("./pages/Sf7Contatto"),
];

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white p-6 text-center" style={{ fontFamily: '"Inter", Arial, sans-serif' }}>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[1.4px] text-[#5B6670]">TRANS RAT · Leonardo Soluzioni</p>
            <p className="mt-3 text-xl font-bold text-[#1e2830]">Si è verificato un errore imprevisto.</p>
            <button className="mt-6 rounded-[10px] bg-[#f2dc3c] px-6 py-3 text-sm font-bold text-[#1e2830] hover:bg-[#e4cb21]" onClick={() => window.location.reload()}>
              Ricarica la pagina
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const LEGACY = { 1: "/funnel", 2: "/funnel/chi-siamo", 3: "/funnel/referenze", 4: "/funnel/come-funziona", 5: "/funnel/soluzione", 6: "/funnel/azienda", 7: "/funnel/contatto" };

function LegacyStep() {
  const params = useParams();
  const target = LEGACY[params.n];
  return target ? <Navigate to={target} replace /> : <Navigate to="/funnel" replace />;
}

function App() {
  useEffect(() => {
    const boot = () => {
      initPixels();
      track("pageview");
    };
    if (getConsent() === "all") boot();
    const onConsent = () => {
      if (getConsent() === "all") boot();
    };
    window.addEventListener("sf-consent-change", onConsent);
    // Chunk delle schermate successive + smooth-scroll partono SOLO ben dopo il primo
    // caricamento completo (load + 1,8 s): zero banda e zero CPU sottratti al rendering.
    const startDeferred = () => {
      setTimeout(() => {
        // Un chunk alla volta, con pause: l'eval non genera long task concatenate.
        PRELOAD_SCREENS.forEach((p, i) => setTimeout(() => p().catch(() => {}), i * 300));
        import("lenis")
        .then(({ default: Lenis }) => {
          if (window.__lenis || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
          const lenis = new Lenis({ lerp: 0.12, smoothWheel: true });
          window.__lenis = lenis;
          let raf = 0;
          const loop = (t) => {
            if (!window.__lenis) return;
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
          };
          raf = requestAnimationFrame(loop);
        })
        .catch(() => {});
      }, 1800);
    };
    if (document.readyState === "complete") startDeferred();
    else window.addEventListener("load", startDeferred, { once: true });
    return () => {
      window.removeEventListener("sf-consent-change", onConsent);
      if (window.__lenis) {
        window.__lenis.destroy();
        window.__lenis = null;
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/funnel" replace />} />
          <Route path="/funnel" element={<SfLayout />}>
            <Route index element={<Sf1Inizia />} />
            <Route path="chi-siamo" element={<Sf2ChiSiamo />} />
            <Route path="referenze" element={<Sf3Referenze />} />
            <Route path="come-funziona" element={<Sf4Video />} />
            <Route path="soluzione" element={<Sf5Soluzione />} />
            <Route path="azienda" element={<Sf6Azienda />} />
            <Route path="contatto" element={<Sf7Contatto />} />
          </Route>
          <Route path="/:n" element={<LegacyStep />} />
          <Route path="/admin" element={<Suspense fallback={null}><Admin /></Suspense>} />
          <Route path="*" element={<Navigate to="/funnel" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
